import { Link } from "@tanstack/react-router";
import { Linkedin } from "lucide-react";
import logoAsset from "@/assets/logo.png.asset.json";

type FooterLink = { label: string; href: string; external?: boolean };

const primaryLinks: FooterLink[] = [
  { label: "About", href: "/about" },
  { label: "Trust Operating System", href: "/trust-operating-system" },
  { label: "CEO Practitioner Network", href: "https://ceopractitionernetwork.com", external: true },
  { label: "Podcast", href: "/podcast" },
  { label: "Conference", href: "/conference" },
  { label: "For Agencies", href: "/for-agencies" },
  { label: "For Organizations", href: "/for-organizations" },
  { label: "Speaking", href: "/book-carlai" },
  { label: "Book Carlai", href: "/book-carlai" },
  { label: "Practitioner Study", href: "https://study.tablesandbridges.com/participate", external: true },
];

const legalLinks: FooterLink[] = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Accessibility", href: "/accessibility" },
];

function FooterLinkItem({ link }: { link: FooterLink }) {
  const cls =
    "text-sm text-warm-white/70 hover:text-gold transition-colors inline-block py-1";
  return (
    <a
      href={link.href}
      target={link.external ? "_blank" : undefined}
      rel={link.external ? "noopener noreferrer" : undefined}
      className={cls}
    >
      {link.label}
    </a>
  );
}

export function Footer() {
  return (
    <footer className="bg-navy text-warm-white border-t border-warm-white/10">
      <div className="container-editorial py-16 md:py-20">
        <div className="grid gap-14 lg:grid-cols-[1.2fr_1fr_1fr]">
          {/* Brand */}
          <div className="max-w-md">
            <Link to="/" className="inline-flex items-center gap-3 mb-6" aria-label="Tables and Bridges - home">
              <img src={logoAsset.url} alt="" width={44} height={44} className="h-11 w-auto" />
              <span className="font-display text-xl tracking-tight">Tables and Bridges</span>
            </Link>
            <p className="text-sm leading-relaxed text-warm-white/75">
              A professional development, training, methodology, and community platform for
              law enforcement Community Engagement professionals, agencies, and the
              organizations that want to build trust from the inside out.
            </p>
            <a
              href="https://www.linkedin.com/in/carlai-moore"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Carlai Moore on LinkedIn"
              className="mt-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-warm-white/25 text-warm-white/70 hover:text-gold hover:border-gold/60 transition-colors"
            >
              <Linkedin className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          {/* Primary nav */}
          <nav aria-label="Footer">
            <p className="eyebrow mb-5">Explore</p>
            <ul className="space-y-1.5">
              {primaryLinks.map((l) => (
                <li key={l.label}>
                  <FooterLinkItem link={l} />
                </li>
              ))}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Legal">
            <p className="eyebrow mb-5">Fine print</p>
            <ul className="space-y-1.5">
              {legalLinks.map((l) => (
                <li key={l.label}>
                  <FooterLinkItem link={l} />
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <hr className="mt-16 border-warm-white/10" />

        <div className="mt-8 flex flex-col-reverse gap-6 md:flex-row md:items-end md:justify-between">
          <p className="text-xs uppercase tracking-[0.2em] text-warm-white/50">
            © {new Date().getFullYear()} TABLES &amp; BRIDGES LLC. ALL RIGHTS RESERVED.
          </p>
          <p className="font-display text-lg md:text-xl text-warm-white/90 leading-snug max-w-sm">
            Tables create trust. <span className="text-gold">Bridges move trust.</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
